import React, { memo, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Button, Typography } from '@mui/material';
import PageBlock from '@sharedCR/common/PageBlock';
import DrawerCDS, { DrawerSize } from '@sharedCR/coreDesignSystem/Drawer/DrawerCDS';
import AiSparkleIcon from 'app/shared/componentsRestyled/coreDesignSystem/icons/AiSparkleIcon';
import PropTypes from 'prop-types';

import { DocumentIntakeJobStatus, DocumentIntakeTerminalStatuses } from 'constants/documentIntake';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import {
  cancelIntakeJobAction,
  clearActiveJobForLoanAction,
  deriveIntakeDisplayStatus,
  selectActiveJobForLoan,
  selectIsCancellingJob,
  startIntakePollAction,
} from 'store/documentIntake';

import StepMiniBars from '../StepMiniBars';

import IntakeProgressDetails from './IntakeProgressDetails';
import {
  cancelButtonStyles,
  failedMetaStyles,
  fullWidthWrapperStyles,
  listStyles,
  rowCompletedStyles,
  rowFailedStyles,
  rowLeadingStyles,
  rowMetaStyles,
  rowStyles,
  rowTrailingStyles,
  textColumnStyles,
} from './IntakeStatusCard.styles';
import { IntakeStatusCardTestIds } from './IntakeStatusCard.testIds';

const INTAKE_CARD_TITLE = 'Document Intake';
const PROGRESS_DRAWER_TITLE = 'Document Intake Progress';

const IntakeStatusCard = ({ loanApplicationId, onOpenReview }) => {
  const dispatch = useDispatch();

  const job = useSelector(selectActiveJobForLoan(loanApplicationId));
  const isCancelling = useSelector(selectIsCancellingJob(job?.id ?? ''));

  const [isProgressDrawerOpen, setIsProgressDrawerOpen] = useState(false);

  const jobId = job?.id ?? null;
  const isJobActive = !!job && !DocumentIntakeTerminalStatuses.has(job.status);

  // Keep the active job fresh independent of the upload modal. SignalR is the
  // primary channel, but the modal that kicked off the initial poll tears it
  // down on unmount — so without a card-owned poll the status stalls at
  // "Processing" until a manual refresh whenever a push event does not arrive.
  // startIntakePollAction stops itself on a terminal status; the cleanup clears
  // it on unmount or when the job leaves the active set.
  useEffectSafeStrictMode(() => {
    if (!jobId || !isJobActive) {
      return undefined;
    }

    const stopPoll = dispatch(startIntakePollAction({ id: jobId }));

    return stopPoll;
  }, [dispatch, jobId, isJobActive]);

  const onCancelClick = useCallback(
    e => {
      e.stopPropagation();

      if (!job?.id) {
        return;
      }

      dispatch(cancelIntakeJobAction({ id: job.id, loanApplicationId }));
    },
    [dispatch, job?.id, loanApplicationId],
  );

  const onCardClick = useCallback(() => {
    if (!job) {
      return;
    }

    if (job.status === DocumentIntakeJobStatus.COMPLETED) {
      onOpenReview?.(job);

      return;
    }

    setIsProgressDrawerOpen(true);
  }, [job, onOpenReview]);

  const onCloseProgressDrawer = useCallback(() => setIsProgressDrawerOpen(false), []);

  const onProgressDrawerOpenReview = useCallback(
    reviewJob => {
      setIsProgressDrawerOpen(false);
      onOpenReview?.(reviewJob);
    },
    [onOpenReview],
  );

  const onDismissFailed = useCallback(() => {
    dispatch(clearActiveJobForLoanAction({ loanApplicationId }));
  }, [dispatch, loanApplicationId]);

  if (!job) {
    return null;
  }

  if (job.status === DocumentIntakeJobStatus.CANCELLED) {
    return null;
  }

  if (job.status === DocumentIntakeJobStatus.FAILED) {
    return (
      <Box sx={fullWidthWrapperStyles}>
        <PageBlock title={INTAKE_CARD_TITLE}>
          <Box sx={listStyles}>
            <Box sx={rowFailedStyles}>
              <Box sx={rowLeadingStyles}>
                <AiSparkleIcon size={18} />
                <Box sx={textColumnStyles}>
                  <Typography variant="medium14">Intake failed</Typography>
                  <Typography sx={failedMetaStyles} variant="regular12">
                    The document could not be processed — try again
                  </Typography>
                </Box>
              </Box>
              <Box sx={rowTrailingStyles}>
                <Button
                  data-testid={IntakeStatusCardTestIds.DISMISS_BUTTON}
                  size="small"
                  sx={cancelButtonStyles}
                  variant="outlined"
                  onClick={onDismissFailed}
                >
                  Dismiss
                </Button>
              </Box>
            </Box>
          </Box>
        </PageBlock>
      </Box>
    );
  }

  const isRunning = !DocumentIntakeTerminalStatuses.has(job.status);
  const isCompleted = job.status === DocumentIntakeJobStatus.COMPLETED;
  const displayLabel = deriveIntakeDisplayStatus(job);
  const segmentsTotal = job.totalSegments ?? 0;
  const segmentsProcessed = job.processedSegments ?? 0;
  const sourceLabel = job.sourceLabel ?? job.uploadLabel ?? null;

  const rowSx = isCompleted ? rowCompletedStyles : rowStyles;

  const segmentCountLabel = `${segmentsTotal} document${segmentsTotal === 1 ? '' : 's'}`;
  const completedTitle = sourceLabel
    ? `Ready to review — ${sourceLabel} (${segmentCountLabel})`
    : `Ready to review (${segmentCountLabel})`;

  return (
    <>
      <Box sx={fullWidthWrapperStyles}>
        <PageBlock title={INTAKE_CARD_TITLE}>
          <Box sx={listStyles}>
            <Box sx={rowSx} onClick={onCardClick}>
              <Box sx={rowLeadingStyles}>
                <AiSparkleIcon size={18} />
                <Box sx={textColumnStyles}>
                  <Typography variant="medium14">
                    {isCompleted ? completedTitle : displayLabel}
                  </Typography>
                  {isRunning &&
                    !(segmentsTotal > 0 && job.status === DocumentIntakeJobStatus.IN_PROGRESS) && (
                      <Typography sx={rowMetaStyles} variant="regular12">
                        {job.status === DocumentIntakeJobStatus.IN_PROGRESS
                          ? 'Processing…'
                          : 'Waiting to start…'}
                      </Typography>
                    )}
                  {isCompleted && (
                    <Typography sx={rowMetaStyles} variant="regular12">
                      Click to review and file
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box sx={rowTrailingStyles}>
                {isRunning && (
                  <StepMiniBars
                    completedSteps={segmentsProcessed}
                    status={
                      job.status === DocumentIntakeJobStatus.IN_PROGRESS ? 'Running' : 'Pending'
                    }
                    totalSteps={segmentsTotal}
                  />
                )}
                {isRunning && (
                  <Button
                    data-testid={IntakeStatusCardTestIds.CANCEL_BUTTON}
                    disabled={isCancelling}
                    size="small"
                    sx={cancelButtonStyles}
                    variant="outlined"
                    onClick={onCancelClick}
                  >
                    Cancel
                  </Button>
                )}
                {isCompleted && (
                  <Button
                    data-testid={IntakeStatusCardTestIds.REVIEW_BUTTON}
                    size="small"
                    sx={cancelButtonStyles}
                    variant="contained"
                    onClick={onCardClick}
                  >
                    Review
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </PageBlock>
      </Box>

      <DrawerCDS
        content={<IntakeProgressDetails job={job} onOpenReview={onProgressDrawerOpenReview} />}
        dataTestId={IntakeStatusCardTestIds.PROGRESS_DRAWER}
        open={isProgressDrawerOpen}
        size={DrawerSize.LARGE}
        title={PROGRESS_DRAWER_TITLE}
        onClose={onCloseProgressDrawer}
      />
    </>
  );
};

IntakeStatusCard.propTypes = {
  loanApplicationId: PropTypes.string,
  onOpenReview: PropTypes.func,
};

export default memo(IntakeStatusCard);
